import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import { useGetRequestsQuery, useUpdateRequestMutation } from "api/TenantSlug/Request/hooks";
import { AffiliateOfferDetailDrawer } from "components/Drawers/AffiliateOfferDetailDrawer/AffiliateOfferDetailDrawer";
import { AcceptRejectTableButtons } from "components/UI/AcceptRejectTableButtons/AcceptRejectTableButtons.tsx";
import { ResponseWithPagination, Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateCell } from "components/UI/Table/UI/DateCell/DateCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useRecoilValue } from "recoil";
import { PermissionEnum, UserRequestStatus } from "types/enums";
import { DrawerId, TableId } from "types/models";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter.ts";
import { getTagDefaultColor } from "utils/getStatusDefaultBackground.ts";

import FormData = App.Data.FormData;
import UserRequestData = App.Data.UserRequestData;
import GetUserRequestsResponse = App.Http.Responses.UserRequest.GetUserRequestsResponse;
import GetUserRequestResponse = App.Http.Responses.UserRequest.GetUserRequestResponse;

interface SearchParams {
    [`affiliateOfferDetail-requestId`]: string;
}

export const AffiliateOfferTable = () => {
    const tableId = TableId.AffiliateOfferTable;
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const permissionUtils = usePermission();
    const updateRequestMutation = useUpdateRequestMutation("");
    const tableAtomInstance = tableAtom<FormData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGeneral } = usePaldockTranslation("general");

    const columnConfig: TableColumnType<UserRequestData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (createdAt: string) => <DateCell value={createdAt} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.date,
        },
        TableColumn.Entity.Offer<UserRequestData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            getEntity: (data) => data.offer,
            width: 150,
        }),
        ...(permissionUtils.hasPermission(PermissionEnum.UserRequestViewForeign)
            ? [
                  TableColumn.Entity.Owner<UserRequestData>({
                      title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
                      dataIndex: "user.nickname",
                      key: "user.nickname",
                      width: 150,
                      filterName: "user_id",
                      sortName: "user.nickname",
                      getEntity: (data) => data.user,
                  }),
              ]
            : []),
        /*{
            title: <ColumnHeaderTitle columnTitle={t("columns.first_name")} />,
            dataIndex: "user_name",
            key: "user_name",
            render: (record) => {
                return record?.name;
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.webs")} />,
            dataIndex: "webs",
            key: "webs",
            render: (webs: string[]) =>
                webs?.length > 0
                    ? webs.slice(0, 2).join(", ") + (webs?.length > 2 ? ` +${webs?.length - 2} more` : "")
                    : t("labels.none"),
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
            dataIndex: "tags",
            key: "tags",
            render: (tags: string[]) =>
                tags?.length > 0
                    ? tags.slice(0, 2).join(", ") + (tags?.length > 2 ? ` +${tags?.length - 2} more` : "")
                    : t("labels.none"),
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "category",
            key: "category",
            inputType: "text",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country",
            key: "country",
            inputType: "text",
            editable: true,
            render: (country: CountryData) => <CountryCell country={country} />,
        },*/
        ...(permissionUtils.hasPermission(PermissionEnum.UserRequestUpdate)
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("state")} />,
                      dataIndex: "status",
                      key: "status",
                      visible: permissionUtils.hasPermission(PermissionEnum.AffiliateEntryEdit),
                      render: (_: undefined, record: UserRequestData) => (
                          <AcceptRejectTableButtons
                              isAccepted={record.status === UserRequestStatus.Accepted}
                              isRejected={record.status === UserRequestStatus.Rejected}
                              onReject={() => handleUpdateRequestMutation(record.id, UserRequestStatus.Rejected)}
                              onAccept={() => handleUpdateRequestMutation(record.id, UserRequestStatus.Accepted)}
                          />
                      ),
                      sorter: true,
                      ellipsis: true,
                      width: 120,
                  },
              ]
            : [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("state")} />,
                      dataIndex: "status",
                      key: "status",
                      render: (status: UserRequestStatus) => (
                          <Tag color={getTagDefaultColor(status)}>{capitalizeFirstLetter(status)}</Tag>
                      ),
                      sorter: true,
                      ellipsis: true,
                      filterType: FILTER_TYPES.select,
                      filterable: true,
                      filterOptions: [
                          { label: tGeneral("request_status.accepted"), value: UserRequestStatus.Accepted },
                          { label: tGeneral("request_status.pending"), value: UserRequestStatus.Pending },
                          { label: tGeneral("request_status.rejected"), value: UserRequestStatus.Rejected },
                      ],
                      width: 100,
                  },
              ]),
    ];

    const fetchRequestsQuery = useGetRequestsQuery(
        {
            page: {
                number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
                size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
            },
            filter: {
                filter: {
                    ...(tableState.filter?.filter ?? {}),
                    ...{
                        type: "offer",
                    },
                },
                include: "user,user.commissionGroups,offer",
                sort: tableState.filter?.sort ?? "-created_at",
            },
        },
        { enabled: false }
    );

    const tableFetchDataUtils = useTableDataApiOperations<
        GetUserRequestsResponse,
        GetUserRequestResponse,
        UserRequestData
    >(tableId, fetchRequestsQuery);

    const handleUpdateRequestMutation = (requestId: string, status: UserRequestStatus) => {
        updateRequestMutation.mutate(
            { requestId, request: { status } },
            {
                onSuccess: () => tableFetchDataUtils.refetchData(),
            }
        );
    };

    const { getRowHandlers, getRowClassName } = useRowHandlers<UserRequestData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.AffiliateOfferDetail,
                drawerParam: `${DrawerId.AffiliateOfferDetail}-requestId`,
                permission: PermissionEnum.AffiliateEntryEdit,
            },
        ],
    });

    const extraFilters = permissionUtils.hasPermission(PermissionEnum.UserRequestUpdate)
        ? [
              {
                  dataIndex: "status",
                  title: tG("state"),
                  filterType: FILTER_TYPES.select,
                  filterName: "status",
                  filterOptions: [
                      { label: tGeneral("request_status.accepted"), value: UserRequestStatus.Accepted },
                      { label: tGeneral("request_status.pending"), value: UserRequestStatus.Pending },
                      { label: tGeneral("request_status.rejected"), value: UserRequestStatus.Rejected },
                  ],
              },
          ]
        : [];

    return (
        <>
            <Table<ResponseWithPagination, UserRequestData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetUserRequestsResponse}
                dataSource={tableFetchDataUtils.tableData?.user_requests || []}
                rowKey={(record) => record.id}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableFilters={true}
                searchDataIndex="offer.title"
                loading={tableFetchDataUtils.fetchQuery.isFetching}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                autoScrollWidth={true}
                extraFilters={extraFilters}
            />
            {searchParams[`${DrawerId.AffiliateOfferDetail}-requestId`] && (
                <AffiliateOfferDetailDrawer requestId={searchParams[`${DrawerId.AffiliateOfferDetail}-requestId`]} />
            )}
        </>
    );
};
